import { formatDate } from "@/lib/dateUtils";
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function UserDetailModal({ userId, open, onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [tempPassword, setTempPassword] = useState(null);

  useEffect(() => {
    if (open && userId) {
      loadUserDetail();
    }
  }, [open, userId]);

  const loadUserDetail = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}/detail`);
      setUser(data);
      setFormData({
        display_name: data.display_name || "",
        legal_name: data.legal_name || "",
        role: data.role || "employee",
        hourly_rate: data.hourly_rate || 0,
        hourly_rate_2: data.hourly_rate_2 || null,
        overtime_rate: data.overtime_rate || null,
        flat_rate: data.flat_rate || null,
        pay_type: data.pay_type || "hourly",
        is_active: data.is_active !== false,
        hire_date: data.hire_date || "",
        original_hire_date: data.original_hire_date || "",
        termination_date: data.termination_date || "",
        birthday: data.birthday || "",
        share_birthday: data.share_birthday !== false,
        development_type: data.development_type || "foundation",
        notes: data.notes || ""
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${userId}`, formData);
      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save user:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm("Reset password for this user?")) return;
    
    try {
      const { data } = await api.post(`/users/${userId}/reset-password`);
      setTempPassword(data.temporary_password);
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Failed to reset password');
    }
  };

  const handleToggleActive = async () => {
    const newStatus = !formData.is_active;
    const action = newStatus ? "activate" : "deactivate";
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    setFormData(prev => ({ ...prev, is_active: newStatus }));
  };

  if (loading || !user) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-8">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User: {user.display_name}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {tempPassword && (
            <Alert>
              <AlertDescription>
                <strong>Temporary Password:</strong> {tempPassword}
                <br />
                <span className="text-xs">Share this with the user. They must change it on first login.</span>
              </AlertDescription>
            </Alert>
          )}

          {/* Identity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Display Name</Label>
              <Input 
                value={formData.display_name} 
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Legal Name (for ADP matching)</Label>
              <Input 
                value={formData.legal_name} 
                onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
              />
            </div>
          </div>

          {/* Role & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(val) => setFormData(prev => ({ ...prev, role: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pay Type</Label>
              <Select value={formData.pay_type} onValueChange={(val) => setFormData(prev => ({ ...prev, pay_type: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="dual_rate">Dual Rate</SelectItem>
                  <SelectItem value="flat_per_period">Flat Per Period</SelectItem>
                  <SelectItem value="contractor_1099">1099 Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Development Type</Label>
              <Select value={formData.development_type || 'foundation'} onValueChange={(val) => setFormData(prev => ({ ...prev, development_type: val }))}>
                <SelectTrigger data-testid="dev-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="foundation">Foundation (trained ground-up)</SelectItem>
                  <SelectItem value="transition">Transition (experienced hire)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Compensation */}
          <div className="grid grid-cols-2 gap-4">
            {formData.pay_type === "hourly" && (
              <div>
                <Label>Hourly Rate</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={formData.hourly_rate} 
                  onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}
            {formData.pay_type === "dual_rate" && (
              <>
                <div>
                  <Label>Rate 1</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.hourly_rate} 
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label>Rate 2</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.hourly_rate_2 || 0} 
                    onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate_2: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </>
            )}
            {formData.pay_type === "flat_per_period" && (
              <div>
                <Label>Flat Rate Per Period</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={formData.flat_rate || 0} 
                  onChange={(e) => setFormData(prev => ({ ...prev, flat_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Hire Date</Label>
              <Input 
                type="date"
                value={formData.hire_date && formData.hire_date !== "NEEDS INPUT" ? formData.hire_date.split("T")[0] : ""} 
                onChange={(e) => setFormData(prev => ({ ...prev, hire_date: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">PTO eligibility: +90 days</p>
            </div>
            <div>
              <Label>Original Hire Date (if rehired)</Label>
              <Input 
                type="date"
                value={formData.original_hire_date && formData.original_hire_date !== "NEEDS INPUT" ? formData.original_hire_date.split("T")[0] : ""} 
                onChange={(e) => setFormData(prev => ({ ...prev, original_hire_date: e.target.value }))}
              />
            </div>
          </div>

          {/* Birthday */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Birthday (MM/DD only)</Label>
              <Input 
                placeholder="MM/DD"
                value={formData.birthday || ""} 
                onChange={(e) => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">No year - age never displayed</p>
            </div>
            <div className="flex items-center space-x-2 mt-6">
              <Switch 
                checked={formData.share_birthday} 
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, share_birthday: checked }))}
              />
              <Label>Share birthday with team</Label>
            </div>
          </div>

          {/* PTO Info (Read-only) */}
          <div className="p-4 bg-muted rounded-lg">
            {user.pto_balance != null && (<>
            <h4 className="font-medium mb-2">PTO Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Accrued</p>
                <p className="font-medium">{user.pto_accrued?.toFixed(2) || 0} hrs</p>
              </div>
              <div>
                <p className="text-muted-foreground">Used</p>
                <p className="font-medium">{user.pto_used?.toFixed(2) || 0} hrs</p>
              </div>
              <div>
                <p className="text-muted-foreground">Balance</p>
                <p className="font-medium">{user.pto_balance?.toFixed(2) || 0} hrs</p>
              </div>
            </div>
            </>)}
            {user.pto_eligibility_date && (
              <p className="text-xs text-muted-foreground mt-2">Eligible since: {formatDate(user.pto_eligibility_date)}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea 
              rows={3}
              value={formData.notes} 
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Button onClick={handleResetPassword} variant="outline">
              Reset Password
            </Button>
            <Button 
              onClick={handleToggleActive} 
              variant={formData.is_active ? "destructive" : "default"}
            >
              {formData.is_active ? "Deactivate Account" : "Activate Account"}
            </Button>
            <Button onClick={onClose} variant="ghost">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
